import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './PersistentVideoPlayer.css';

interface PersistentVideoPlayerProps {
  videoUrl: string;
  title?: string;
  isVisible: boolean;
  onClose: () => void;
  onShow?: () => void;
}

const PersistentVideoPlayer: React.FC<PersistentVideoPlayerProps> = ({
  videoUrl,
  title,
  isVisible,
  onClose,
  onShow
}) => {
  // DISABLED: Component is temporarily disabled and hidden
  return null;

  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({ 
    isDragging: false, 
    startX: 0, 
    startY: 0, 
    initialX: 0, 
    initialY: 0 
  });

  // Use translation for title if not provided
  const displayTitle = title || t('videoPlayer.title');

  useEffect(() => {
    // Auto-play video when component mounts
    if (videoRef.current && isVisible) {
      videoRef.current.play().catch(console.error);
    }
  }, [isVisible]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).classList.contains('video-header')) {
      setIsDragging(true);
      dragRef.current.isDragging = true;
      dragRef.current.startX = e.clientX;
      dragRef.current.startY = e.clientY;
      dragRef.current.initialX = position.x;
      dragRef.current.initialY = position.y;
      e.preventDefault();
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragRef.current.isDragging) return;

    const deltaX = e.clientX - dragRef.current.startX;
    const deltaY = e.clientY - dragRef.current.startY;

    const newX = dragRef.current.initialX + deltaX;
    const newY = dragRef.current.initialY + deltaY;

    // Constrain to viewport
    const maxX = window.innerWidth - (playerRef.current?.offsetWidth || 320);
    const maxY = window.innerHeight - (playerRef.current?.offsetHeight || 240);

    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    dragRef.current.isDragging = false;
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  // Show toggle button when video player is not visible
  if (!isVisible && onShow) {
    return (
      <button 
        className="video-player-toggle"
        onClick={onShow}
        title={t('videoPlayer.showTooltip')}
        style={{ outline: 'none', border: 'none' }}
      >
        <img src="/play-circle.svg" alt={t('videoPlayer.playAlt')} width="20" height="20" style={{ marginRight: '6px' }} />
        {t('videoPlayer.toggleButton')}
      </button>
    );
  }

  if (!isVisible) return null;

  return (
    <div 
      ref={playerRef}
      className={`persistent-video-player ${isDragging ? 'dragging' : ''}`}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`
      }}
    >
      <div 
        className="video-header"
        onMouseDown={handleMouseDown}
      >
        <span className="video-title">{displayTitle}</span>
        <div className="video-controls">
          <button 
            className="close-btn"
            onClick={onClose}
            title={t('videoPlayer.closeTooltip')}
          >
            ×
          </button>
        </div>
      </div>
      
      <div className="video-content">
        <video 
          ref={videoRef}
          controls
          muted
          loop
          preload="metadata"
          className="video-element"
        >
          <source src={videoUrl} type="video/mp4" />
          {t('videoPlayer.browserNotSupported')}
        </video>
      </div>
    </div>
  );
};

export default PersistentVideoPlayer; 